import { motion, AnimatePresence } from 'framer-motion';
import type { SLDSelectedNode } from './utils/sldTypes';
import { GeneratorDetailCard } from './panels/GeneratorDetailCard';
import { BESSDetailCard } from './panels/BESSDetailCard';
import { MeterDetailCard } from './panels/MeterDetailCard';
import { formatVoltage } from '../../utils/formatVoltage';

interface SLDDetailPanelProps {
  siteId?: string;
  selectedNode: SLDSelectedNode | null;
  onClose: () => void;
  onViewDetails: (assetType: string) => void;
}

const NODE_META: Record<string, { label: string; assetType?: string }> = {
  generator: { label: 'Generator', assetType: 'generator' },
  bess: { label: 'Battery Storage', assetType: 'battery_storage' },
  solar: { label: 'Solar PV', assetType: 'solar_pv' },
  transformer: { label: 'Transformer' },
  breaker: { label: 'Circuit Breaker' },
  meter: { label: 'Meter' },
  load: { label: 'Load' },
  busBar: { label: 'Bus Bar' },
  evCharger: { label: 'EV Charger', assetType: 'ev_charging' },
};

// Device types that have rich Gamma cards
const RICH_CARD_TYPES = new Set(['generator', 'bess', 'meter']);

// ── Legacy inline components for non-redesigned device types ──

function MetricRow({ label, value, unit }: { label: string; value: unknown; unit?: string }) {
  if (value == null) return null;
  const display = typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 1 }) : String(value);
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-ems-border">
      <span className="text-xs text-ems-text-muted">{label}</span>
      <span className="text-sm text-ems-text-primary font-mono">{display}{unit ? ` ${unit}` : ''}</span>
    </div>
  );
}

function LegacyStatusBadge({ status }: { status: string }) {
  const colorMap: Record<string, string> = {
    running: 'bg-ems-green/20 text-ems-green',
    charging: 'bg-ems-cyan/20 text-ems-cyan',
    discharging: 'bg-ems-cyan/20 text-ems-cyan',
    generating: 'bg-ems-amber/20 text-ems-amber',
    closed: 'bg-ems-green/20 text-ems-green',
    open: 'bg-ems-red/20 text-ems-red',
    tripped: 'bg-ems-red/20 text-ems-red',
    active: 'bg-ems-green/20 text-ems-green',
    normal: 'bg-ems-blue/20 text-ems-blue',
    fault: 'bg-ems-red/20 text-ems-red',
    offline: 'bg-ems-text-muted/20 text-ems-text-muted',
    idle: 'bg-ems-text-muted/20 text-ems-text-secondary',
  };
  const cls = colorMap[status] ?? 'bg-ems-text-muted/20 text-ems-text-secondary';
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls}`}>{status.toUpperCase()}</span>;
}

function SolarMetrics({ data }: { data: Record<string, unknown> }) {
  return (
    <>
      <MetricRow label="Active Power" value={data.activePowerKw} unit="kW" />
      <MetricRow label="Rated Capacity" value={data.ratedCapacityKw} unit="kW" />
      <MetricRow
        label="Voltage"
        value={data.voltageV != null ? formatVoltage(Number(data.voltageV), 'dc') : undefined}
      />
      <MetricRow label="Current" value={data.currentA} unit="A" />
    </>
  );
}

function TransformerMetrics({ data }: { data: Record<string, unknown> }) {
  return (
    <>
      <MetricRow label="HV Side" value={data.hvVoltage} />
      <MetricRow label="LV Side" value={data.lvVoltage} />
      <MetricRow label="Load" value={data.loadPercent} unit="%" />
      <MetricRow label="Rated" value={data.ratedKva} unit="kVA" />
    </>
  );
}

function BreakerMetrics({ data }: { data: Record<string, unknown> }) {
  return (
    <>
      <MetricRow label="State" value={data.state} />
      <MetricRow label="Last Operation" value={data.lastOperationAt} />
      <MetricRow label="Trip Count" value={data.tripCount} />
    </>
  );
}

function LoadMetrics({ data }: { data: Record<string, unknown> }) {
  return (
    <>
      <MetricRow label="Consumption" value={data.activePowerKw} unit="kW" />
      <MetricRow label="Peak Demand" value={data.peakDemandKw} unit="kW" />
    </>
  );
}

function EVChargerMetrics({ data }: { data: Record<string, unknown> }) {
  return (
    <>
      <MetricRow label="Active Power" value={data.activePowerKw} unit="kW" />
      <MetricRow label="Active Sessions" value={data.activeSessions} />
      <MetricRow label="Ports Available" value={data.portsAvailable} />
      <MetricRow label="Total Ports" value={data.portCount} />
    </>
  );
}

const LEGACY_METRIC_COMPONENTS: Record<string, React.FC<{ data: Record<string, unknown> }>> = {
  solar: SolarMetrics,
  transformer: TransformerMetrics,
  breaker: BreakerMetrics,
  load: LoadMetrics,
  evCharger: EVChargerMetrics,
};

// ── Legacy panel layout for non-redesigned types ──

function LegacyPanelContent({
  selectedNode,
  meta,
  onClose,
  onViewDetails,
}: {
  selectedNode: SLDSelectedNode;
  meta: { label: string; assetType?: string };
  onClose: () => void;
  onViewDetails: (assetType: string) => void;
}) {
  const MetricsComponent = LEGACY_METRIC_COMPONENTS[selectedNode.nodeType];
  const status = (selectedNode.data.status ?? selectedNode.data.state ?? selectedNode.data.mode ?? 'unknown') as string;

  return (
    <>
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-ems-border flex-shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-ems-text-primary">{(selectedNode.data.label ?? meta.label) as string}</h3>
          <button onClick={onClose} className="text-ems-text-muted hover:text-ems-text-primary text-lg leading-none">&times;</button>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-ems-text-muted">{meta.label}</span>
          <LegacyStatusBadge status={status} />
        </div>
        {selectedNode.data.deviceId && (
          <span className="text-[10px] text-ems-text-muted font-mono mt-1 block opacity-60">{selectedNode.data.deviceId as string}</span>
        )}
      </div>

      {/* Metrics */}
      <div className="flex-1 overflow-y-auto p-4">
        {MetricsComponent && <MetricsComponent data={selectedNode.data} />}
      </div>

      {/* Footer */}
      {meta.assetType && (
        <div className="px-4 py-3 border-t border-ems-border flex-shrink-0">
          <button
            onClick={() => onViewDetails(meta.assetType!)}
            className="w-full py-2 px-4 bg-ems-forest border border-[rgba(52,211,153,0.2)] rounded-lg text-sm text-ems-green font-medium transition-colors hover:bg-ems-forest/80 hover:border-ems-border-bright"
          >
            View Details &rarr;
          </button>
        </div>
      )}
    </>
  );
}

// ── Main Panel ──

export default function SLDDetailPanel({ siteId, selectedNode, onClose, onViewDetails }: SLDDetailPanelProps) {
  if (!selectedNode) return null;

  const meta = NODE_META[selectedNode.nodeType] ?? { label: selectedNode.nodeType };
  const isRichCard = RICH_CARD_TYPES.has(selectedNode.nodeType);

  const handleViewDetails = () => {
    if (meta.assetType) {
      onViewDetails(meta.assetType);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key={selectedNode.nodeId}
        initial={{ x: 320, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        exit={{ x: 320, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="absolute top-0 right-0 w-80 h-full bg-ems-bg-card border-l border-ems-border z-10 flex flex-col overflow-hidden"
      >
        {isRichCard ? (
          <>
            {/* Close button overlaid for rich cards */}
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-20 w-7 h-7 flex items-center justify-center rounded-lg text-ems-text-muted hover:text-ems-text-primary hover:bg-ems-bg-card-hover transition-colors"
            >
              &times;
            </button>

            {selectedNode.nodeType === 'generator' && (
              <GeneratorDetailCard siteId={siteId} data={selectedNode.data} onViewDetails={handleViewDetails} />
            )}
            {selectedNode.nodeType === 'bess' && (
              <BESSDetailCard siteId={siteId} data={selectedNode.data} onViewDetails={handleViewDetails} />
            )}
            {selectedNode.nodeType === 'meter' && (
              <MeterDetailCard siteId={siteId} data={selectedNode.data} onViewDetails={handleViewDetails} />
            )}
          </>
        ) : (
          <LegacyPanelContent
            selectedNode={selectedNode}
            meta={meta}
            onClose={onClose}
            onViewDetails={onViewDetails}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}
